import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import TextInput from '../NumberTextInput';
import Modal from '../modal/Modal';
import { Col, Row } from '../../lib/utils';
import { PaymentHistoryType } from '../../services/useMembershipQueryService';
import NewCalendar from '../Calendar/NewCalendar';
import dayjs from 'dayjs';
import { BoldText, DemiLightText, MediumText, RegularText } from '../../lib/Text';
import { SalesStatus } from '../../services/useMembershipPaymentsQueryService';
import useMembershipPaymentsMutationService from '../../services/useMembershipPaymentsMutationService';
import { resolveSharePoolAdminError } from '../../features/sharedMembership/errors';
import { useQueryClient } from '@tanstack/react-query';

const PAYMENT_MUTATION_FALLBACK = '요청을 처리하지 못했습니다. 잠시 후 다시 시도해주세요.';

type Props = {
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    paymentHistory?: PaymentHistoryType;
    createAlert?: any;
    /** 부모 페이지가 전달. 'group'이면 MemberTicketPayment 전용 엔드포인트로 분기 */
    source?: 'pt' | 'group';
    centerId?: string;
    /** 지금 보고 있는 수강권. 결제행이 다른 세대에 기록돼 있으면 회차 적용 대상이 달라진다. */
    viewingMembershipId?: string;
};

function ModalPaymentHistoryUpdate({
    paymentHistory,
    setIsVisible,
    createAlert,
    source,
    centerId,
    viewingMembershipId,
}: Props) {
    // const title = type === 'refund' ? '수강권 환불' : '수강권 업그레이드';
    //'COMPLETED' | 'UPGRADED' | 'PARTIAL_REFUND' | 'FULL_REFUND' | 'PARTIAL_PAYMENT'
    const queryClient = useQueryClient();
    const { patchMembershipPaymentMutation, deleteMembershipPaymentMutation } = useMembershipPaymentsMutationService({
        onPatchMembershipPaymentSuccess() {
            queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] });
            // createAlert('입력 오류', '추가 가능한 시간을 초과하였습니다', '확인', () => {});
            createAlert('수정 완료', '수정이 완료되었습니다', '확인', () => {});

            setIsVisible(false);
        },
        onDeleteMembershipPaymentSuccess() {
            queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] });
            createAlert('삭제 완료', '삭제가 완료되었습니다', '확인', () => {});
            setIsVisible(false);
        },
        // 공유 중인 수강권의 결제 이력은 서버가 409로 막는다. 모달을 닫지 않고 원인만 알린다.
        onPatchMembershipPaymentFail(result) {
            createAlert('수정 실패', resolveSharePoolAdminError(result, PAYMENT_MUTATION_FALLBACK).message, true);
        },
        onDeleteMembershipPaymentFail(result) {
            createAlert('삭제 실패', resolveSharePoolAdminError(result, PAYMENT_MUTATION_FALLBACK).message, true);
        },
    });
    const type = paymentHistory?.saleStatus as SalesStatus;
    const membershipPaymentId = paymentHistory?._id as string;
    const title =
        type === 'FULL_REFUND'
            ? '전체 환불 수정'
            : type === 'UPGRADED'
            ? '업그레이드 금액 수정'
            : type === 'PARTIAL_REFUND'
            ? '환불 금액 수정'
            : type === 'PARTIAL_PAYMENT'
            ? '부분 결제 금액 수정'
            : '완료 결제 수정';

    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    // 환불결제일자 div ref
    const divRef = React.useRef<HTMLDivElement>(null);
    // div 위치
    const [divPosition, setDivPosition] = useState({ top: 0, left: 0 });
    const [date, setDate] = useState(paymentHistory?.paymentDate || dayjs().toISOString());
    // 부분활불일땐 cancelAmount
    const [price, setPrice] = useState(
        (type === 'PARTIAL_REFUND' ? paymentHistory?.cancelAmount : paymentHistory?.paymentAmount) || 0
    );
    // 회차
    const [count, setCount] = useState(paymentHistory?.totalSessionOffset || 0);

    const priceTitle =
        type === 'FULL_REFUND'
            ? '전체 환불 금액(취소액)'
            : type === 'UPGRADED'
            ? '업그레이드 결제 금액(추가결제)'
            : type === 'PARTIAL_REFUND'
            ? '부분 환불 금액(취소액)'
            : type === 'PARTIAL_PAYMENT'
            ? '부분 결제 금액'
            : '완료 결제 금액';

    const dateTitle =
        type === 'FULL_REFUND'
            ? '전체 환불 일시'
            : type === 'UPGRADED'
            ? '업그레이드 결제 일시'
            : type === 'PARTIAL_REFUND'
            ? '부분 환불 일시'
            : type === 'PARTIAL_PAYMENT'
            ? '부분 결제 일시'
            : '완료 결제 일시';

    const countTitle =
        type === 'FULL_REFUND'
            ? '전체 환불할 회차 수'
            : type === 'UPGRADED'
            ? '업그레이드한 회차 수(추가한 회차 수)'
            : type === 'PARTIAL_REFUND'
            ? '부분 환불한 회차 수'
            : type === 'PARTIAL_PAYMENT'
            ? '부분 결제 회차'
            : '완료 결제 회차';

    useEffect(() => {
        if (divRef.current) {
            const rect = divRef.current.getBoundingClientRect();
            setDivPosition({ top: rect.top + rect.height, left: rect.left });
        }
    }, [divRef]);

    /**
     * 수정 요청 payload. Enter 와 버튼이 같은 것을 보내야 한다.
     *
     * 예전 Enter 경로는 offset 을 빼먹었는데, 서버가 `offset ?? 0` 으로 읽어
     * 회차 오프셋이 0 이 되고 그만큼 회차가 되돌아갔다.
     */
    const buildUpdatePayload = () => ({
        type,
        amount: price,
        date,
        offset: type === 'COMPLETED' || type === 'PARTIAL_PAYMENT' ? undefined : count,
        membershipPaymentId,
        source,
        centerId,
    });

    // 삭제는 두 번 눌러야 실행된다. 되돌릴 수 없는 동작이라 오클릭을 막는다.
    const [isDeleteConfirming, setIsDeleteConfirming] = useState(false);

    /**
     * A6 — 이 결제행이 **다른 세대**에 기록돼 있는가.
     *
     * 결제행 수정은 회차를 그 결제행의 `membershipId`(= 원판매 수강권)에 적용한다. 인계 계보
     * 결제 목록은 여러 세대의 행을 한 표에 섞어 보여주므로, 지금 보고 있는 수강권이 아니라
     * **저쪽 문서의 회차가 움직인다.** 화면에는 아무 변화가 없어서 알아채기 어렵다.
     *
     * `membershipId` 는 계보 결제 목록에만 실린다. 단건 조회에서 온 행은 전부 이 수강권 것이라
     * 값이 없고, 그때는 경고하지 않는다.
     */
    const paymentRowMembershipId = paymentHistory?.membershipId
        ? String(paymentHistory.membershipId)
        : '';
    const isCrossGenerationRow =
        !!paymentRowMembershipId &&
        !!viewingMembershipId &&
        paymentRowMembershipId !== String(viewingMembershipId);

    // 엔터씨 수정하기
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            patchMembershipPaymentMutation.mutate(buildUpdatePayload());
        }
    };

    return (
        <Modal
            setIsVisible={setIsVisible}
            whiteViewWidth={500}
            borderRadius={25}
            isCloseOutside={false}
            isCloseButton
            modalCommpont={
                isCalendarOpen ? (
                    <NewCalendar
                        date={dayjs(date)}
                        selectedDay={dayjs(date)}
                        setIsCalendar={setIsCalendarOpen}
                        onClickDate={(day) => {
                            setDate(dayjs(day).toISOString());
                        }}
                        isAllDate={false}
                        top={divPosition.top}
                        left={divPosition.left + 16}
                        isNextMonth
                        isPrevMonth
                        titleFormat="YYYY년 MM월"
                    />
                ) : undefined
            }
            onClose={() => {
                setIsVisible(false);
            }}
        >
            <Col className="flex w-full h-full p-[35px]">
                <Row className="flex items-center mb-[23px]">
                    <MediumText fontSize={28} lineHeight={38} color="#3D4244" className="mr-[31px]">
                        {title}
                    </MediumText>
                </Row>
                {isCrossGenerationRow && (
                    <Col className="mb-[20px] rounded-[8px] border border-solid border-[#FDBA74] bg-[#FFF7ED] px-[14px] py-[10px]">
                        <RegularText fontSize={13} lineHeight={19} color="#C2410C">
                            담당강사 인계 전 수강권에 기록된 결제입니다. 회차를 수정하면 지금 보고 있는
                            수강권이 아니라 그 결제가 기록된 수강권의 회차가 바뀝니다. 금액만 정정하려면
                            회차 값을 그대로 두세요.
                        </RegularText>
                    </Col>
                )}
                <Col ref={divRef} className="flex flex-col mb-[33px]">
                    <RegularText fontSize={16} lineHeight={23} color="#505967" className="mb-[12px]">
                        {dateTitle}
                    </RegularText>
                    <Col
                        className={`flex  border  border-solid h-[67px] justify-center rounded-[10px] bg-white px-[18px] cursor-pointer hover:border-primary transition-colors duration-300 ease-in-out ${
                            isCalendarOpen ? 'border-primary' : 'border-[#D9D9D9B2]'
                        }`}
                        onClick={() => {
                            setIsCalendarOpen(true);
                        }}
                    >
                        <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                            {dayjs(date).format('YYYY-MM-DD')}
                        </RegularText>
                    </Col>
                </Col>
                <Col className="flex flex-col w-full  justify-between">
                    <Col className={`${type !== 'COMPLETED' ? 'mb-[33px]' : 'mb-[48px]'}`}>
                        <RegularText fontSize={16} lineHeight={23} color="#505967" className="mb-[12px]">
                            {priceTitle}
                        </RegularText>
                        <TextInput
                            height={67}
                            value={price}
                            onChange={(value) => {
                                setPrice(value as number);
                            }}
                            comma
                            unit="원"
                            placeholder="0"
                            width={116}
                            onKeyDown={handleKeyDown}
                        />
                    </Col>
                    {type !== 'COMPLETED' && type !== 'PARTIAL_PAYMENT' && (
                        <Col className="mb-[48px]">
                            <RegularText fontSize={16} lineHeight={23} color="#505967" className="mb-[12px]">
                                {countTitle}
                            </RegularText>
                            <TextInput
                                height={67}
                                value={count}
                                onChange={(value) => {
                                    setCount(value as number);
                                }}
                                unit="회"
                                placeholder="0"
                                width={116}
                                onKeyDown={handleKeyDown}
                            />
                        </Col>
                    )}

                    <Row className="self-end">
                        <Col
                            className="flex justify-center items-center  w-[110px] h-[48px]  bg-[#F2F4F6] rounded-[9px] cursor-pointer mr-[13px]"
                            onClick={() => {
                                /**
                                 * 결제 이력 목록에 인계 계보의 다른 세대 결제행까지 섞여 나오고
                                 * 전부 클릭할 수 있다. 확인 없이 지우면 오클릭 한 번에 다른
                                 * 수강권의 매출 기록이 사라지고 되돌릴 수 없다.
                                 * 한 번 더 눌러야 실제로 지워진다.
                                 */
                                if (!isDeleteConfirming) {
                                    setIsDeleteConfirming(true);
                                    return;
                                }
                                deleteMembershipPaymentMutation.mutate({
                                    membershipPaymentId,
                                    source,
                                    centerId,
                                });
                            }}
                        >
                            <RegularText
                                fontSize={16}
                                lineHeight={20}
                                color={isDeleteConfirming ? '#B4232D' : '#3D4244'}
                            >
                                {isDeleteConfirming ? '정말 삭제' : '삭제하기'}
                            </RegularText>
                        </Col>
                        <Col
                            className="flex justify-center items-center  w-[110px] h-[48px]  bg-primary rounded-[9px] cursor-pointer"
                            onClick={() => {
                                const offset = type === 'COMPLETED' || type === 'PARTIAL_PAYMENT' ? undefined : count;

                                patchMembershipPaymentMutation.mutate({
                                    type,
                                    amount: price,
                                    date,
                                    offset,
                                    membershipPaymentId,
                                    source,
                                    centerId,
                                });
                            }}
                        >
                            <RegularText fontSize={16} lineHeight={20} color="white">
                                수정하기
                            </RegularText>
                        </Col>
                    </Row>
                </Col>
            </Col>
        </Modal>
    );
}

export default ModalPaymentHistoryUpdate;

